import React, { useRef, useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Modal,
  Tooltip
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Fullscreen as FullscreenIcon,
  PhotoCamera as PhotoCameraIcon,
  ScreenRotation as ScreenRotationIcon,
  ZoomIn as ZoomInIcon,
  FullscreenExit as ZoomOutIcon,
  Fullscreen as FullscreenIconMUI,
  ZoomIn as ZoomInIconMUI,
  ZoomOut as ZoomOutIconMUI
} from '@mui/icons-material';
import ReactDOM from 'react-dom';
import { Cctv, CctvStatus } from '../../types/cctv';
import { SimpleCctv, SIMPLE_DUMMY_CAMERAS } from '../../mock/cctv';
import { useNavigate } from 'react-router-dom';
import { useCctvStore } from '../../stores/cctvStore';
import Hls from 'hls.js';

// CCTV 타입 추가
type CctvType = 'safety' | 'management' | 'all';

interface CctvFeedProps {
  cctv?: Cctv;
  poolId?: number;
  cameraId?: number;
  title?: string;
  compactMode?: boolean;
  onFullscreen?: () => void;
  onScreenshot?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  isZoomed?: boolean;
  isInitiallyMuted?: boolean;
  onError?: (error: string) => void;
}

const CCTVFeed: React.FC<CctvFeedProps> = ({
  cctv,
  poolId,
  cameraId,
  onFullscreen,
  onScreenshot,
  onZoomIn,
  onZoomOut,
  isZoomed = false,
  isInitiallyMuted = true,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const resolvedStreamUrl = cctv?.stream_url || '';
  const resolvedCctvId = cctv?.id ?? cameraId ?? 0;
  const resolvedPoolId = cctv?.pool_id ?? poolId ?? 0;

  useEffect(() => {
    if (!resolvedStreamUrl) {
      setError('스트림 URL이 없음');
      setIsLoading(false);
      return;
    }

    if (!videoRef.current) return;

    const video = videoRef.current;
    video.muted = isInitiallyMuted;

    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        maxLoadingDelay: 4,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90
      });

      hlsRef.current = hls;

      hls.loadSource(resolvedStreamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(err => {
          console.error('비디오 재생 실패:', err);
          setError('비디오 재생에 실패');
          if (onError) onError('비디오 재생에 실패');
        });
        setIsLoading(false);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('네트워크 오류:', data);
              setError('스트림 연결에 실패');
              if (onError) onError('스트림 연결에 실패');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('미디어 오류:', data);
              setError('스트림 재생에 실패');
              if (onError) onError('스트림 재생에 실패');
              hls.recoverMediaError();
              break;
            default:
              console.error('치명적 오류:', data);
              setError('스트림 처리 중 오류 발생');
              if (onError) onError('스트림 처리 중 오류 발생');
              hls.destroy();
              break;
          }
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari 네이티브 HLS 지원
      video.src = resolvedStreamUrl;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(err => {
          console.error('비디오 재생 실패:', err);
          setError('비디오 재생에 실패');
          if (onError) onError('비디오 재생에 실패');
        });
        setIsLoading(false);
      });
    } else {
      setError('이 브라우저는 HLS 스트리밍을 지원하지 않음');
      if (onError) onError('이 브라우저는 HLS 스트리밍을 지원하지 않음');
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [resolvedStreamUrl, isInitiallyMuted, onError]);

  const handleZoomIn = () => {
    // 새 창에서 CCTV 열기
    const url = `/cctv-view?cctvId=${resolvedCctvId}&poolId=${resolvedPoolId}`;
    window.open(url, '_blank', 'width=800,height=600');
  };

  const rotate180 = Boolean(cctv?.rotate_180);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: 1,
          transform: rotate180 ? 'rotate(180deg)' : undefined,
        }}
      >
        {isLoading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1
          }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1
          }}>
            <Alert severity="error" sx={{ maxWidth: '80%' }}>
              {error}
            </Alert>
          </Box>
        )}

        <Box
          component="video"
          ref={videoRef}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          autoPlay
          muted
          playsInline
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          display: 'flex',
          gap: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 1,
          padding: 0.5,
        }}
      >
        <Tooltip title="새 창에서 크게 보기">
          <IconButton
            size="small"
            onClick={handleZoomIn}
            sx={{ color: 'white' }}
          >
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="전체화면">
          <IconButton
            size="small"
            onClick={onFullscreen}
            sx={{ color: 'white' }}
          >
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="스크린샷">
          <IconButton
            size="small"
            onClick={onScreenshot}
            sx={{ color: 'white' }}
          >
            <PhotoCameraIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default CCTVFeed; 